import { useQuery, useMutation, useQueryClient } from 'react-query';
import { message } from 'antd';
import {
  promotionService,
  type Promotion,
  type CreatePromotionRequest,
  type UpdatePromotionRequest,
  type GetPromotionsParams,
  type ApplyPromotionRequest,
} from '../services/promotionservice';

// =====================================================
// 📝 Query Keys
// =====================================================

export const promotionKeys = {
  all: ['promotions'] as const,
  lists: () => [...promotionKeys.all, 'list'] as const,
  list: (params: GetPromotionsParams) => [...promotionKeys.lists(), params] as const,
  details: () => [...promotionKeys.all, 'detail'] as const,
  detail: (id: string) => [...promotionKeys.details(), id] as const,
  active: () => [...promotionKeys.all, 'active'] as const,
  stats: () => [...promotionKeys.all, 'stats'] as const,
  applicable: (courseId: string) => [...promotionKeys.all, 'applicable', courseId] as const,
};

// =====================================================
// 📝 Queries
// =====================================================

/**
 * ດຶງລາຍການໂປຣໂມຊັ່ນທັງໝົດ (Admin)
 */
export const usePromotions = (params: GetPromotionsParams = {}) => {
  return useQuery({
    queryKey: promotionKeys.list(params),
    queryFn: () => promotionService.getPromotions(params),
    keepPreviousData: true,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * ດຶງໂປຣໂມຊັ່ນທີ່ກຳລັງໃຊ້ງານ (Public)
 */
export const useActivePromotions = () => {
  return useQuery({
    queryKey: promotionKeys.active(),
    queryFn: () => promotionService.getActivePromotions(),
    staleTime: 60000, // 1 minute
  });
};

/**
 * ດຶງຂໍ້ມູນໂປຣໂມຊັ່ນຕາມ ID
 */
export const usePromotion = (id: string, enabled = true) => {
  return useQuery({
    queryKey: promotionKeys.detail(id),
    queryFn: () => promotionService.getPromotion(id),
    enabled: !!id && enabled,
    staleTime: 30000,
  });
};

/**
 * ດຶງສະຖິຕິໂປຣໂມຊັ່ນ
 */
export const usePromotionStats = () => {
  return useQuery({
    queryKey: promotionKeys.stats(),
    queryFn: () => promotionService.getPromotionStats(),
    staleTime: 60000, // 1 minute
  });
};

/**
 * ກວດໂປຣໂມຊັ່ນທີ່ໃຊ້ໄດ້ກັບຄອສ
 */
export const useApplicablePromotions = (courseId: string, enabled = true) => {
  return useQuery({
    queryKey: promotionKeys.applicable(courseId),
    queryFn: () => promotionService.getApplicablePromotions(courseId),
    enabled: !!courseId && enabled,
    staleTime: 30000,
  });
};

// =====================================================
// 📝 Mutations
// =====================================================

/**
 * ສ້າງໂປຣໂມຊັ່ນໃໝ່
 */
export const useCreatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePromotionRequest) => promotionService.createPromotion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.active() });
      message.success('✅ ສ້າງໂປຣໂມຊັ່ນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(`❌ ${error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ'}`);
    },
  });
};

/**
 * ອັບເດດໂປຣໂມຊັ່ນ
 */
export const useUpdatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromotionRequest }) =>
      promotionService.updatePromotion(id, data),
    onSuccess: (_:any, variables:any) => {
      queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: promotionKeys.active() });
      message.success('✅ ອັບເດດໂປຣໂມຊັ່ນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(`❌ ${error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ'}`);
    },
  });
};

/**
 * ລຶບໂປຣໂມຊັ່ນ
 */
export const useDeletePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => promotionService.deletePromotion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.active() });
      message.success('✅ ລຶບໂປຣໂມຊັ່ນສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(`❌ ${error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ'}`);
    },
  });
};

/**
 * ເປີດ/ປິດ ສະຖານະໂປຣໂມຊັ່ນ
 */
export const useTogglePromotionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => promotionService.togglePromotionStatus(id),
    onSuccess: (_:any, id:any) => {
      queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: promotionKeys.active() });
      queryClient.invalidateQueries({ queryKey: promotionKeys.stats() });
      message.success('✅ ອັບເດດສະຖານະສຳເລັດ');
    },
    onError: (error: any) => {
      message.error(`❌ ${error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ'}`);
    },
  });
};

/**
 * ຄຳນວນສ່ວນຫຼຸດ (Apply Promotion)
 */
export const useApplyPromotion = () => {
  return useMutation({
    mutationFn: (data: ApplyPromotionRequest) => promotionService.applyPromotion(data),
    onError: (error: any) => {
      message.error(`❌ ${error.response?.data?.message || 'ບໍ່ສາມາດນຳໃຊ້ໂປຣໂມຊັ່ນໄດ້'}`);
    },
  });
};

// =====================================================
// 📝 Helper Hooks
// =====================================================

/**
 * ກວດວ່າໂປຣໂມຊັ່ນກຳລັງໃຊ້ງານຢູ່ບໍ່
 */
export const useIsPromotionCurrent = (promotion: Promotion | undefined) => {
  if (!promotion) return false;

  const now = new Date();
  const startDate = new Date(promotion.startDate);
  const endDate = new Date(promotion.endDate);

  return (
    promotion.isActive &&
    startDate <= now &&
    endDate >= now &&
    (promotion.maxUsage === null || promotion.currentUsage < promotion.maxUsage)
  );
};

/**
 * ຄິດໄລ່ສ່ວນຫຼຸດ (Client-side calculation)
 */
export const useCalculateDiscount = () => {
  return (
    promotion: Promotion | undefined,
    subtotal: number,
    currency: 'LAK' | 'THB' | 'USD' = 'LAK'
  ): { discountAmount: number; finalAmount: number } => {
    if (!promotion) {
      return { discountAmount: 0, finalAmount: subtotal };
    }

    let discountAmount = 0;

    if (promotion.discountType === 'percentage') {
      discountAmount = (subtotal * promotion.discountPercentage) / 100;
    } else {
      discountAmount = promotion.discountAmount[currency] || 0;
    }

    const finalAmount = Math.max(0, subtotal - discountAmount);

    return { discountAmount, finalAmount };
  };
};

// =====================================================
// 📝 Export All
// =====================================================

export default {
  // Queries
  usePromotions,
  useActivePromotions,
  usePromotion,
  usePromotionStats,
  useApplicablePromotions,
  
  // Mutations
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
  useTogglePromotionStatus,
  useApplyPromotion,
  
  // Helpers
  useIsPromotionCurrent,
  useCalculateDiscount,
};